// Mat Selector — question config + weighted scoring model.
// Scoring tags live on each mat (from /api/mats); this scores them against answers.

export const JOB_LABEL = {
  construction: "Heavy / civil construction",
  "oil-gas": "Oil & gas / drilling",
  "utility-pipeline": "Utility / pipeline",
  crane: "Crane operation",
  event: "Event / venue",
  agricultural: "Agricultural",
  emergency: "Emergency / disaster response",
  landscaping: "Landscaping / turf protection",
  other: "General / other",
};

export const TERRAIN_LABEL = {
  "soft-mud": "soft soil / mud",
  sand: "sand",
  "grass-turf": "grass / turf",
  gravel: "gravel",
  wetland: "wetland / marsh",
  "snow-ice": "snow / ice",
  paved: "paved / concrete",
};

export const LOAD_LABEL = {
  foot: "foot-traffic-only",
  atv: "ATV / UTV",
  pickup: "pickup truck",
  medium: "medium equipment",
  heavy: "heavy equipment",
};

export const DURATION_LABEL = {
  short: "short-term (days)",
  medium: "medium-term (weeks)",
  long: "long-term (months+)",
  permanent: "permanent / semi-permanent",
};

const LOAD_RANK = { foot: 1, atv: 2, pickup: 3, medium: 4, heavy: 5 };

export const QUESTIONS = [
  {
    id: "job", kind: "single", title: "What kind of job is this?",
    subtitle: "Pick the closest match — it shapes the whole recommendation.",
    options: [
      { value: "construction", label: "Heavy / civil construction" },
      { value: "oil-gas", label: "Oil & gas / drilling" },
      { value: "utility-pipeline", label: "Utility / pipeline" },
      { value: "crane", label: "Crane operation" },
      { value: "event", label: "Event / venue" },
      { value: "agricultural", label: "Agricultural" },
      { value: "emergency", label: "Emergency / disaster response" },
      { value: "landscaping", label: "Landscaping / turf protection" },
      { value: "other", label: "Something else" },
    ],
  },
  {
    id: "terrain", kind: "single", title: "What's the ground like?",
    subtitle: "The terrain you need to protect or cross.",
    options: [
      { value: "soft-mud", label: "Soft soil / mud" },
      { value: "sand", label: "Sand" },
      { value: "grass-turf", label: "Grass / turf" },
      { value: "gravel", label: "Gravel" },
      { value: "wetland", label: "Wetland / marsh" },
      { value: "snow-ice", label: "Snow / ice" },
      { value: "paved", label: "Paved / concrete" },
    ],
  },
  {
    id: "load", kind: "single+text", title: "What will travel or sit on the mats?",
    subtitle: "Choose the heaviest load — this drives load rating.",
    options: [
      { value: "foot", label: "Foot traffic only" },
      { value: "atv", label: "ATV / UTV" },
      { value: "pickup", label: "Pickup truck" },
      { value: "medium", label: "Medium equipment", hint: "Skid steer, forklift" },
      { value: "heavy", label: "Heavy equipment", hint: "Crane, excavator, drilling rig" },
    ],
    textKey: "maxWeight", textLabel: "Approx. max weight (optional)",
    textPlaceholder: "e.g. 40 tons or 80,000 lbs",
  },
  {
    id: "duration", kind: "single", title: "How long will they be in place?",
    subtitle: "Duration affects durability vs. cost trade-offs.",
    options: [
      { value: "short", label: "Short-term", hint: "Days" },
      { value: "medium", label: "Medium-term", hint: "Weeks" },
      { value: "long", label: "Long-term", hint: "Months+" },
      { value: "permanent", label: "Permanent / semi-permanent" },
    ],
  },
  {
    id: "coverage", kind: "text", title: "Roughly how much coverage do you need?",
    subtitle: "Optional — helps us size and price your quote.",
    textKey: "coverage", textLabel: "Approx. square footage or number of mats",
    textPlaceholder: "e.g. 5,000 sq ft or ~40 mats",
  },
  {
    id: "budget", kind: "dual", title: "How do you want to acquire them?",
    subtitle: "We can sell you the product or run it as a turnkey service.",
    groups: [
      {
        key: "acquisition", label: "Buy or rent?",
        options: [
          { value: "rent", label: "Rent / turnkey service" },
          { value: "purchase", label: "Purchase / own" },
        ],
      },
      {
        key: "priority", label: "What matters more?",
        options: [
          { value: "budget", label: "Budget-conscious" },
          { value: "premium", label: "Premium durability" },
        ],
      },
    ],
  },
  {
    id: "environment", kind: "dual", title: "How sensitive is the site?",
    subtitle: "Sensitive ground steers us toward non-toxic, gap-free coverage.",
    groups: [
      {
        key: "sensitive", label: "Protecting sensitive vegetation / wetlands?",
        options: [
          { value: "yes", label: "Yes — sensitive site" },
          { value: "no", label: "No — standard site" },
        ],
      },
      {
        key: "coverageNeed", label: "Coverage needed",
        options: [
          { value: "full", label: "Full coverage (no gaps)" },
          { value: "partial", label: "Partial is fine" },
        ],
      },
    ],
  },
  {
    id: "weather", kind: "single", title: "What conditions will you face?",
    subtitle: "Weather influences traction, handling, and material choice.",
    options: [
      { value: "wet-muddy", label: "Wet / muddy season" },
      { value: "freezing", label: "Freezing conditions" },
      { value: "extreme-heat", label: "Extreme heat" },
      { value: "mild", label: "Mild / varies" },
    ],
  },
];

export function isStepComplete(step, a) {
  const q = QUESTIONS[step];
  if (!q) return true;
  if (q.kind === "single" || q.kind === "single+text") return !!a[q.id];
  if (q.kind === "dual") return !!a[q.groups[0].key];
  if (q.kind === "text") return true; // optional
  return true;
}

function parseWeightTons(s) {
  if (!s) return null;
  const str = String(s).toLowerCase();
  const m = str.match(/[\d,.]+/);
  if (!m) return null;
  const n = parseFloat(m[0].replace(/,/g, ""));
  if (!n) return null;
  if (str.includes("lb") || str.includes("pound") || n > 200) return n / 2000; // assume lbs if huge
  return n; // tons
}

function scoreMat(mat, a) {
  const has = (arr, v) => Array.isArray(arr) && arr.includes(v);
  let score = 0;
  const reasons = [];
  let disq = false;

  // LOAD — most heavily weighted
  let effLoad = a.load;
  const tons = parseWeightTons(a.maxWeight);
  if (tons != null) {
    if (tons >= 20) effLoad = "heavy";
    else if (tons >= 3 && (!effLoad || LOAD_RANK[effLoad] < LOAD_RANK.medium)) effLoad = "medium";
  }
  if (effLoad) {
    const need = LOAD_RANK[effLoad];
    if (has(mat.loads, effLoad)) { score += 4; reasons.push(`Rated for your ${LOAD_LABEL[effLoad]} loads`); }
    if (need >= 4 && mat.load_rating === "Heavy") score += 3;
    if (need >= 4 && mat.load_rating === "Light") { score -= 6; disq = true; }
    if (effLoad === "heavy" && mat.load_rating === "Medium") score -= 3;
    if (need <= 2 && mat.load_rating === "Light") { score += 2; reasons.push("Right-sized for light loads — no over-spec"); }
  }

  // TERRAIN
  if (a.terrain && has(mat.terrains, a.terrain)) { score += 3; reasons.push(`Suited to ${TERRAIN_LABEL[a.terrain]}`); }
  if (a.terrain === "paved" && has(mat.functions, "trench")) score += 2;
  if ((a.terrain === "soft-mud" || a.terrain === "wetland") && has(mat.functions, "trench")) score -= 2;

  // JOB
  if (a.job && has(mat.job_types, a.job)) { score += 3; reasons.push(`Commonly specified for ${JOB_LABEL[a.job].toLowerCase()}`); }
  if (a.job === "crane" && has(mat.functions, "crane-pad")) { score += 3; reasons.push("Designed for crane / outrigger point loads"); }
  if (a.job === "oil-gas" && has(mat.functions, "rig")) score += 2;

  // DURATION
  if (a.duration && has(mat.durations, a.duration)) { score += 2; reasons.push(`Built for ${DURATION_LABEL[a.duration]} deployments`); }

  // ENVIRONMENTAL SENSITIVITY
  if (a.sensitive === "yes") {
    if (mat.env_sensitive) { score += 3; reasons.push("Non-toxic & non-leaching — safe for sensitive vegetation"); }
    else score -= 2;
  }
  if (a.coverageNeed === "full" && mat.load_rating === "Light" && has(mat.functions, "turf")) score += 1;

  // BUDGET
  if (a.acquisition === "rent" && mat.rent_friendly) score += 1;
  if (a.priority === "budget") {
    if (mat.load_rating === "Light" || mat.load_rating === "Medium") { score += 2; reasons.push("Cost-effective for budget-conscious, rental-friendly jobs"); }
    else score -= 1;
  }
  if (a.priority === "premium" && mat.load_rating === "Heavy") { score += 1; reasons.push("Maximum durability for demanding, long-run use"); }

  // WEATHER
  if (a.weather === "wet-muddy" && has(mat.terrains, "soft-mud")) score += 1;
  if (a.weather === "freezing" && mat.load_rating === "Heavy") score += 1;

  if (reasons.length === 0) reasons.push("A solid general-purpose option for your inputs");
  return { score: Math.max(score, 0), reasons: reasons.slice(0, 3), disq };
}

const MAX_SCORE = 16;

export function scoreMats(mats, answers) {
  const scored = (mats || []).map((mat) => {
    const { score, reasons, disq } = scoreMat(mat, answers);
    return { mat, score, reasons, disq, pct: Math.min(Math.round((score / MAX_SCORE) * 100), 98) };
  });
  let ranked = scored.filter((s) => !s.disq && s.score > 0).sort((a, b) => b.score - a.score);
  if (ranked.length === 0) ranked = scored.sort((a, b) => b.score - a.score); // fallback
  const top = ranked.slice(0, 3).map((s, i) => ({
    ...s,
    label: i === 0 ? "Best Fit" : "Good Alternative",
  }));
  return top;
}

export function buildSelectionSummary(answers, recommendations) {
  const L = [];
  L.push("MAT SELECTOR RESULTS");
  L.push(`Job: ${JOB_LABEL[answers.job] || "—"}`);
  L.push(`Terrain: ${TERRAIN_LABEL[answers.terrain] || "—"}`);
  L.push(`Load: ${LOAD_LABEL[answers.load] || "—"}${answers.maxWeight ? ` (~${answers.maxWeight})` : ""}`);
  L.push(`Duration: ${DURATION_LABEL[answers.duration] || "—"}`);
  if (answers.coverage) L.push(`Coverage: ${answers.coverage}`);
  L.push(`Acquisition: ${answers.acquisition === "purchase" ? "Purchase / own" : answers.acquisition === "rent" ? "Rent / turnkey" : "—"}`);
  L.push(`Sensitive site: ${answers.sensitive === "yes" ? "Yes" : "No"}`);
  L.push(`Conditions: ${answers.weather || "—"}`);
  L.push("");
  L.push("RECOMMENDED:");
  (recommendations || []).forEach((r, i) => {
    L.push(`${i + 1}. ${r.mat.name} — ${r.label} (${r.pct}% match)`);
    r.reasons.forEach((why) => L.push(`   • ${why}`));
  });
  return L.join("\n");
}
